// oxlint-disable-next-line no-restricted-imports
import { ActionIcon, TextInput } from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons-react";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";

import classes from "./SearchBar.module.css";

type SearchBarSize = "small" | "large";

type SearchBarProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: SearchBarSize;
  onClear?: () => void;
  onSearch?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
};

const SearchBar = ({
  value,
  onChange,
  placeholder = "",
  disabled,
  size = "small",
  onClear,
  onSearch,
  onKeyDown,
  autoFocus,
}: SearchBarProps) => {
  const isLarge = size === "large";
  const isFilled = value.length > 0;
  const showClearButton = isFilled && Boolean(onClear) && !disabled;
  const backgroundColor = isLarge ? colors.bg.elevation.primary : colors.bg.elevation.tertiary;
  const activeBackgroundColor =
    !isLarge && isFilled ? colors.bg.elevation.secondary : backgroundColor;
  const hoverBorderColor =
    isLarge && !isFilled ? colors.border.tertiary : colors.border.secondaryDefault;
  const iconColor = disabled ? colors.icon.primaryDisabled : colors.icon.primaryDefault;

  return (
    <TextInput
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onSearch?.();
        }
        onKeyDown?.(e);
      }}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      radius={isLarge ? radius.xxl : radius.lg}
      leftSection={
        <span className={classes.leadingIcon}>
          <IconSearch size={16} color={iconColor} />
        </span>
      }
      leftSectionWidth={spacing[9]}
      leftSectionPointerEvents="none"
      rightSection={
        showClearButton && onClear ? (
          <span className={classes.trailingSlot}>
            <ClearButton onClear={onClear} />
          </span>
        ) : null
      }
      rightSectionWidth={showClearButton ? spacing[9] : undefined}
      classNames={{ wrapper: classes.wrapper, input: classes.input }}
      style={{
        width: "100%",
        "--input-bg": backgroundColor,
        "--input-bd": colors.border.tertiary,
        "--input-color": colors.text.primaryDefault,
        "--input-placeholder-color": colors.text.secondaryDefault,
        "--input-radius": isLarge ? radius.xxl : radius.lg,
        "--input-height": isLarge ? spacing[10] : spacing[9],
        "--input-fz": fontSizes[14],
        "--sb-bg": backgroundColor,
        "--sb-border": colors.border.tertiary,
        "--sb-hover-border": hoverBorderColor,
        "--sb-active-border": colors.border.primaryDefault,
        "--sb-active-bg": activeBackgroundColor,
        "--sb-disabled-bg": colors.bg.elevation.secondary,
        "--sb-disabled-border": colors.border.secondaryDisabled,
        "--sb-color": colors.text.primaryDefault,
        "--sb-placeholder": colors.text.secondaryDefault,
        "--sb-disabled-color": colors.text.secondaryDisabled,
        "--sb-radius": isLarge ? radius.xxl : radius.lg,
        "--sb-height": isLarge ? spacing[10] : spacing[9],
        "--sb-padding-end": showClearButton ? spacing[9] : isLarge ? spacing[3] : spacing[2],
        "--sb-font-size": fontSizes[14],
        "--sb-font-weight": fontWeights.regular,
      }}
    />
  );
};

const ClearButton = ({ onClear }: { onClear: () => void }) => (
  <ActionIcon
    type="button"
    variant="filled"
    size={spacing[5]}
    radius={radius.sm}
    onClick={onClear}
    aria-label="Clear search"
    style={{
      "--ai-bg": colors.bg.button.close.default,
      "--ai-hover": colors.bg.button.close.hover,
      "--ai-color": colors.icon.primaryDefault,
      "--ai-bd": "none",
    }}
  >
    <IconX size={10} />
  </ActionIcon>
);

export { SearchBar };
